/**
 * <CodeInput> — la saisie d'un code à usage unique (atom).
 *
 * Six cases qu'on LIT, un seul champ qu'on REMPLIT : le vrai champ est posé par-dessus la rangée,
 * invisible, et les cases ne font que montrer sa valeur. C'est ce qui garde le code en un geste —
 * un code collé entier, le code que le système propose depuis le SMS ou l'e-mail, et le retour
 * arrière fonctionnent comme dans un champ ordinaire, ce qu'une rangée de six champs ne sait pas
 * faire. Un lecteur d'écran n'entend qu'un champ, « Code à 6 chiffres ».
 *
 * Pas de `maxLength` natif, à dessein : un « 123 456 » collé fait sept caractères et serait coupé
 * avant que son espace soit retiré. Le composant garde les chiffres, puis coupe.
 */
import React, { useState } from "react"
import { Platform, TextInput as RNTextInput, View, type TextInputProps as RNTextInputProps } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Stack } from "../containers/Stack"
import { fieldContour } from "../TextInput"
import { ErrorText } from "../typography/ErrorText"
import { Text } from "../typography/Text"

/** La hauteur d'une case — plus haute qu'un champ (40) : un chiffre s'y lit de loin. */
const CODE_CELL_HEIGHT = 52
/** Au-delà, les cases s'étaleraient en tuiles : la rangée s'arrête et se centre. */
const CODE_CELL_MAX_WIDTH = 56

export type CodeInputProps = {
    /** Les chiffres saisis, sans espace. */
    value: string
    /** Reçoit les chiffres seuls, coupés à `length` — quoi qu'on ait tapé ou collé. */
    onChangeText: (digits: string) => void
    /** Nombre de chiffres — 6 par défaut. */
    length?: number
    /** Message d'erreur — passe les cases au rouge et s'affiche dessous. */
    error?: string
    /** Le code ne sert plus (expiré, trop d'essais) : cases estompées, saisie bloquée. */
    disabled?: boolean
    testID?: string
} & Pick<RNTextInputProps, "autoFocus" | "onSubmitEditing">

const onlyDigits = (text: string, length: number) => text.replace(/\D/g, "").slice(0, length)

export function CodeInput(props: CodeInputProps) {
    const { value, onChangeText, length = 6, error, disabled = false, testID, ...rest } = props

    const [focused, setFocused] = useState(false)
    const digits = onlyDigits(value, length)
    // La case qui attend le prochain chiffre ; la dernière quand le code est complet.
    const activeIndex = Math.min(digits.length, length - 1)

    return (
        <Stack gap="2">
            <View style={styles.row(disabled)}>
                {Array.from({ length }, (_, index) => {
                    const active = focused && !disabled && index === activeIndex
                    const borderKey: SemanticColorKey = error != null ? "danger" : active ? "primary" : "border"
                    const digit = digits[index]

                    return (
                        <View
                            key={index}
                            style={[
                                fieldContour.frame(borderKey, disabled ? "surface-subtle" : "surface-raised", false),
                                styles.cell,
                            ]}
                            // Les cases sont un dessin : le champ, lui, porte le nom et la valeur.
                            accessible={false}
                            importantForAccessibility="no-hide-descendants"
                            aria-hidden
                        >
                            {digit != null ? (
                                <Text size="text-2xl" weight="bold" color="text-strong">
                                    {digit}
                                </Text>
                            ) : (
                                active && <View style={styles.caret} />
                            )}
                        </View>
                    )
                })}

                <RNTextInput
                    testID={testID}
                    style={styles.input}
                    value={digits}
                    onChangeText={(text) => onChangeText(onlyDigits(text, length))}
                    editable={!disabled}
                    keyboardType="number-pad"
                    textContentType="oneTimeCode"
                    autoComplete={Platform.OS === "android" ? "sms-otp" : "one-time-code"}
                    autoCorrect={false}
                    caretHidden
                    selectionColor="transparent"
                    accessibilityLabel={`Code à ${length} chiffres`}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    {...rest}
                />
            </View>

            {error != null && (
                <ErrorText center live>
                    {error}
                </ErrorText>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: (disabled: boolean) => ({
        flexDirection: "row" as const,
        justifyContent: "center" as const,
        gap: theme.spacing["2"],
        opacity: disabled ? 0.5 : 1,
    }),
    cell: {
        flex: 1,
        maxWidth: CODE_CELL_MAX_WIDTH,
        height: CODE_CELL_HEIGHT,
        alignItems: "center",
        justifyContent: "center",
    },
    /** Le trait qui dit où tombera le prochain chiffre — fixe : « le texte ne bouge jamais ». */
    caret: {
        width: 2,
        height: CODE_CELL_HEIGHT / 2.5,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.primary,
    },
    /**
     * Le vrai champ couvre toute la rangée : toucher n'importe quelle case le prend. Son texte
     * est transparent (les cases le montrent), mais il n'est ni masqué ni à opacité nulle — un
     * champ que le système ne voit pas ne reçoit pas le code du SMS.
     *
     * Pas `"transparent"` : la couleur se convertit en 0, qu'Android lit comme « aucune couleur »,
     * et le texte y revenait en noir par-dessus la première case (vu en recette, 19/10). Un blanc
     * d'alpha nul vaut une autre valeur et reste invisible partout.
     */
    input: {
        position: "absolute" as const,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        color: "rgba(255, 255, 255, 0)",
        fontSize: theme.fontSizes["text-base"],
        ...(Platform.OS === "web" ? ({ outlineStyle: "none", caretColor: "transparent" } as object) : null),
    },
}))
